import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";
import { mapState } from "pinia";
import { PayerSequenceCodes, PayerTypes } from "../../../../../../../enums/enums";
import { ToastrService } from "../../../../../../../services/toastr.service";
import { formatDate } from "../../../../../../../common/vue-helpers/modifiers/value-modifier";
import { HospiceConvertToPendingPatient } from "../../../../../../../resources-vue/vue-models/hospice-convertToPending-details";
import { HospicePatientPayer } from "../../../../../../../resources-vue/vue-models/hospice-patient-payer";
import { useHospiceConvertToPending } from "../../../../store/useHospiceConvertToPending";

import PayerForm from "../PayerForm/PayerForm.vue";
import TablePrompt from "../../../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import EmptyTextHandler from "../../../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export default defineComponent({
    components: {
        PayerForm,
        TablePrompt,
        EmptyTextHandler
    },
    props: {
        payer: {
            type: HospicePatientPayer
        },
        patientDetails: {
            type: HospiceConvertToPendingPatient
        },
        columns: {
            type: Number
        },
        payorTypeEnum: {
            default: PayerTypes
        },
        resetList: {
            type: Function
        },
        medicareIds: {
            type: Array,
            default: []
        },
        updatePatientPayer: {
            type: Function
        },
        deletePatientPayer: {
            type: Function
        }
    },

    data(vm) {
        return {
            _toastrService: null,
            cachedData: null,
            isEditRequested: false as boolean,
            isEditInProgress: false as boolean,
            isDeleteRequested: false as boolean,
            isDeleteInProgress: false as boolean,
            payerSequenceCodeEnum: PayerSequenceCodes,
            formatDate
        };
    },
    created() {
        this._toastrService = Container.instance.get(ToastrService);
    },
    mounted() {
        this.cachedData = new HospicePatientPayer(this.payer);
    },
    computed: {
        ...mapState(useHospiceConvertToPending, ["patientId"]),
        billAsTypeLabel() {
            if (!this.payerSequenceCodeEnum || !this.payer || !this.payer.BillAsType) {
                return null;
            }
            return this.payerSequenceCodeEnum.getDescriptionUsingValue(this.payer.BillAsType);
        },
        payorTypeLabel() {
            if (!this.payorTypeEnum || !this.payer) {
                return null;
            }
            return this.payorTypeEnum?.getDescriptionUsingValue(this.payer.Type);
        },
        isUpcoming() {
            if (this.payer.EffectiveDate) {
                return moment() < moment(this.payer.EffectiveDate);
            } else {
                return false;
            }
        }
    },
    methods: {
        openEditPayer() {
            this.isEditRequested = true;
            this.cachedData = new HospicePatientPayer(this.payer);
        },
        async updatePayer() {
            const validated = await (this.$refs.payerForm as any).v$.$validate();
            if (validated) {
                try {
                    this.isEditInProgress = true;
                    let updateResponse = await this.updatePatientPayer(this.payer.Id, this.cachedData);
                    this.payer.Id = updateResponse.data.id;
                    this.closeEditPayer();
                    this._toastrService.success({
                        title: `Payer Updated`,
                        message: `The Payer <b>${this.payer.AgencyPayorName}</b> has been successfully updated.`
                    });
                    this.resetList();
                } catch (err) {
                    this.openEditPayer();
                    this._toastrService.error({
                        title: `Payer Updated`,
                        message: ` ${JSON.parse(err.message)}}`
                    });
                } finally {
                    this.isEditInProgress = false;
                }
            } else {
                this._toastrService.error({
                    title: `Validation Error`,
                    message: `Pleaae fill required fields`
                });
            }
        },
        closeEditPayer() {
            this.isEditRequested = false;
            this.cachedData = undefined;
        },
        confirmDeletePayer() {
            this.isDeleteRequested = true;
        },
        async deletePayer() {
            this.isDeleteInProgress = true;
            const delResult = await this.deletePatientPayer(this.payer.Id);
            if (delResult.isSuccess) {
                this.isDeleteRequested = false;
                this.resetList();
                this._toastrService.success({
                    message: `The Payer ${this.payer.AgencyPayorName} has been successfully deleted.`,
                    title: `Payer Deleted`
                });
            } else {
                this._toastrService.error({
                    title: "Failed to delete Payer",
                    message: `${JSON.parse(delResult.message).message}`
                });
                this.confirmDeletePayer();
                this.resetList();
            }
        },
        cancelDeletePayer() {
            this.isDeleteRequested = false;
        }
    }
});
